import { DropdownItem } from 'components/dropdowns/base/base';
import { SelectDropdown } from 'components/dropdowns/select/select';
import { wccModules } from 'enums/wccModules';
import { inject, injectable } from 'inversify';
import { Subscribable } from 'knockout';
import { CurrentUserManager } from 'managers/currentUser';
import { IWCCStorageManager } from 'managers/iStorage';
import { withEffect } from 'mixins/withEffect';
import { EffectsContainer } from 'mixins/withEffects';
import { Person } from 'models/person';
import { Topic } from 'models/topic';
import ThreadEditorVM from './thread';
import './visibilityButton.html';
import './visibilityButton.scss';

components.preload('wcc-select');

const allVisibilities = {
    'public': <ThreadVisibility>{
        value: enums.ThreadVisibility.Public.value,
        title: labels.Public,
        icon: '&#xe371;',
        description: labels.OtherParticipantsInTheDiscussionCanSeeThisPost
    },

    'private': <ThreadVisibility>{
        value: enums.ThreadVisibility.Private.value,
        title: labels.PrivatePost,
        icon: '&#xe025;',
        description: labels.ThisPostIsOnlyVisibleToYouAndTheModerator
    },

    'group': <ThreadVisibility>{
        value: enums.ThreadVisibility.Group.value,
        title: labels.ByPerson,
        icon: '&#xe044;',
        description: labels.ThisPostIsOnlyVisibleToYouAndParticipantsYouHaveSelected
    },

    'tags': <ThreadVisibility>{
        value: enums.ThreadVisibility.Tags.value,
        title: labels.ByTag,
        icon: '&#xe067;',
        description: labels.ThisPostIsOnlyVisibleToYouAndParticipantsTaggedByTags
    },

    'privateMessage': <ThreadVisibility>{
        value: enums.ThreadVisibility.PrivateMessage.value,
        title: labels.PrivateMessage,
        icon: '&#xe245;',
        description: labels.ThisPostIsAMessageOrQuestionToTheModerator,
        separate: true
    },

    'privateModAndObs': <ThreadVisibility>{
        value: enums.ThreadVisibility.PrivateModsAndObs.value,
        title: labels.PrivatePost,
        icon: '&#xe025;',
        description: labels.ThisPostIsOnlyVisibleToModeratorsAndObservers,
        separate: true
    },
}

interface ThreadVisibility {
    value: number
    title: string
    icon: string
    description: string
    separate?: boolean
}

export interface ThreadEditorVisibilityButtonVMConfig {
    editor: ThreadEditorVM
}

@injectable()
export default class ThreadEditorVisibilityButtonVM {
    private topic: Subscribable<Topic | undefined>
    private user: Subscribable<Person | undefined>
    private visibilities = ko.pureComputed(() => this.getVisibilities())

    editor: ThreadEditorVM
    visibilitiesSelect: SelectDropdown<number>

    constructor(
        @inject(wccModules.componentConfig) config: ThreadEditorVisibilityButtonVMConfig,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.storage) storage: IWCCStorageManager,
        @inject(wccModules.effects) effects: EffectsContainer
    ) {
        this.editor = config.editor;

        this.topic = this.editor.topic;

        const userManager = storage.get(CurrentUserManager, { discussionId: settings.discussionId });
        this.user = userManager.pluck(m => m.person);

        this.visibilitiesSelect = new SelectDropdown<number>({
            items: this.visibilities.map(visibility => visibility as DropdownItem<number>)
        });

        const selectedVisibility = this.visibilitiesSelect.selectedItem.pluck('value');

        const isReady = ko.pureComputed(() => this.checkIfReady());

        effects.register(isReady => {
            if (isReady) {
                return [
                    withEffect(visibility => {
                        if (selectedVisibility() !== visibility)
                            this.visibilitiesSelect.select(item => item.value === visibility);
                    }, [this.editor.threadVisibility]),

                    withEffect(selectedVisibility => {
                        if (selectedVisibility !== this.editor.threadVisibility())
                            this.editor.threadVisibility(selectedVisibility)
                    }, [selectedVisibility])
                ]
            }
        }, [isReady]);

        ko.applyBindingsToNode(info.element, {
            'visible': isReady,

            'css': {
                'thread-editor-visibility-button': true
            }
        }, this);
    }

    private checkIfReady() {
        return this.visibilities().length > 1;
    }

    private getVisibilities() {
        const topic = this.topic();
        const user = this.user();

        const visibilities: Array<ThreadVisibility> = [];

        if (topic != undefined && user != undefined) {
            visibilities.push(allVisibilities.public);

            if (user.isModerator()) {
                visibilities.push(allVisibilities.group);
                visibilities.push(allVisibilities.tags);
                visibilities.push(allVisibilities.privateModAndObs);
            } else if (user.isRegular() && !topic.commentsAreHidden() && !topic.isIdeation()) {
                visibilities.push(allVisibilities.private);
                visibilities.push(allVisibilities.privateMessage);
            }
        }

        return visibilities;
    }
}